import React from 'react';
import Footer from "../views/Footer";
import FadeIn from "../components/FadeIn";
import { Container, Typography, Card } from "../components/ui";

const WhyChooseUs = () => {
  return (
    <>
      {/* Hero Section */}
      <Container.Section padding="hero" background="gradient">
        <Container size="standard">
          <FadeIn delay={0.2} direction="down" fullWidth>
            <Typography.SectionHeader
              title={
                <>
                  Why Choose <span className="text-gradient">BimBytes Academy</span>?
                </>
              }
              description="We provide industry-driven BIM training that bridges the gap between theory and real-world application, preparing you for high-demand careers in the AEC industry."
            />
          </FadeIn>
        </Container>
      </Container.Section>

      {/* Main Features Grid */}
      <Container.Section padding="content" background="white">
        <Container size="standard">
          <Container.Grid columns="3" gap="default" className="items-stretch [&>*]:h-full">
            {[
              { icon: "🏗️", title: "Real-time Live Projects", description: "Training based on actual ongoing international projects for practical experience." },
              { icon: "🛠️", title: "Industry Tools", description: "Master Revit, Navisworks, Dynamo and other essential BIM software." },
              { icon: "💼", title: "Placement Support", description: "Dedicated assistance to help you secure positions in top AEC firms." },
              { icon: "👶", title: "Beginner-Friendly", description: "Courses designed to take you from scratch to an industry professional." },
              { icon: "❓", title: "Complete Doubt Clearance", description: "Personalized attention to ensure all your technical queries are resolved." },
              { icon: "🏢", title: "Industrial-Oriented Training", description: "Curriculum aligned with current industry demands and standards." },
              { icon: "🔄", title: "Flexible Course Module", description: "Customizable learning paths to suit your career goals." },
              { icon: "💻", title: "Online & Offline Mode", description: "Choose between classroom training or interactive live online sessions." },
              { icon: "👨‍🏫", title: "Expert Trainers", description: "Learn from professionals with 10+ years of global industry experience." }
            ].map((feature, index) => (
              <FadeIn key={index} delay={0.2 + index * 0.1} direction="up">
                <Card variant="feature" padding="default" className="h-full flex flex-col items-center text-center">
                  <Card.Icon size="default">
                    <span className="text-3xl">{feature.icon}</span>
                  </Card.Icon>
                  
                  <Card.Title size="default" className="group-hover:text-primary transition-colors duration-300 ease-smooth">
                    {feature.title}
                  </Card.Title>
                  
                  <Card.Content>
                    {feature.description}
                  </Card.Content>
                </Card>
              </FadeIn>
            ))}
          </Container.Grid>
        </Container>
      </Container.Section>

      {/* Detailed Sections */}
      <Container.Section padding="content" background="gradient">
        <Container size="standard">
          {/* Placement Support Detail */}
          <FadeIn delay={0.2} direction="left">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary mb-6">
                  Comprehensive <span className="text-primary">Placement Support</span>
                </h2>
                <p className="text-lg text-textSecondary leading-relaxed mb-4">
                  Our dedicated placement cell works tirelessly to connect our students with top AEC firms and BIM consultancies. We provide end-to-end support from resume building to interview preparation.
                </p>
                <ul className="space-y-3 text-textSecondary">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Resume and portfolio development workshops</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Mock interviews with industry experts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Direct referrals to partner companies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Job placement assistance until you get hired</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl ring-1 ring-primary/10">
                <div className="grid grid-cols-2 gap-6 text-center">
                  <div className="p-4">
                    <div className="text-4xl font-bold text-primary mb-2">500+</div>
                    <div className="text-sm text-textSecondary">Students Placed</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold text-primary mb-2">50+</div>
                    <div className="text-sm text-textSecondary">Hiring Partners</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold text-primary mb-2">90%</div>
                    <div className="text-sm text-textSecondary">Placement Rate</div>
                  </div>
                  <div className="p-4">
                    <div className="text-4xl font-bold text-primary mb-2">100%</div>
                    <div className="text-sm text-textSecondary">Support</div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Flexible Course Modules Detail */}
          <FadeIn delay={0.3} direction="right">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div className="order-2 lg:order-1">
                <div className="bg-white rounded-2xl p-8 shadow-xl ring-1 ring-primary/10">
                  <h3 className="text-2xl font-bold text-textPrimary mb-6 text-center">Course Delivery Modes</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">💻</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-textPrimary">Online Mode</h4>
                        <p className="text-sm text-textSecondary">Live interactive sessions with recorded backups</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">🏢</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-textPrimary">Offline Mode</h4>
                        <p className="text-sm text-textSecondary">Classroom training at our Chennai center</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                        <span className="text-2xl">🔄</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-textPrimary">Hybrid Mode</h4>
                        <p className="text-sm text-textSecondary">Mix of online and offline as per your convenience</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary mb-6">
                  Flexible <span className="text-primary">Course Modules</span>
                </h2>
                <p className="text-lg text-textSecondary leading-relaxed mb-4">
                  We understand that every student has unique needs and schedules. That's why we offer flexible learning options that adapt to your lifestyle and career goals.
                </p>
                <ul className="space-y-3 text-textSecondary">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Choose your preferred learning mode (Online/Offline/Hybrid)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Flexible timing with weekend and weekday batches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Self-paced learning with recorded sessions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Customizable curriculum based on your goals</span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>

          {/* Real-time Live Projects Detail */}
          <FadeIn delay={0.4} direction="left">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary mb-6">
                  Training Based on <span className="text-primary">Real-time Live Projects</span>
                </h2>
                <p className="text-lg text-textSecondary leading-relaxed mb-4">
                  At BIM Bytes Academy, we believe in learning by doing. Our students work on actual ongoing international projects, gaining hands-on experience that sets them apart in the job market.
                </p>
                <ul className="space-y-3 text-textSecondary">
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Work on live projects from Dubai, India, and other global locations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Understand real-world challenges and solutions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Build a professional portfolio with actual project work</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary mt-1">✓</span>
                    <span>Learn industry best practices and workflows</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl ring-1 ring-primary/10">
                <h3 className="text-2xl font-bold text-textPrimary mb-6 text-center">Project Exposure</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl text-center">
                    <div className="text-3xl font-bold text-primary mb-1">20+</div>
                    <div className="text-xs text-textSecondary">Live Projects</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl text-center">
                    <div className="text-3xl font-bold text-primary mb-1">5+</div>
                    <div className="text-xs text-textSecondary">Countries</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl text-center">
                    <div className="text-3xl font-bold text-primary mb-1">100+</div>
                    <div className="text-xs text-textSecondary">Project Hours</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl text-center">
                    <div className="text-3xl font-bold text-primary mb-1">10+</div>
                    <div className="text-xs text-textSecondary">Project Types</div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </Container>
      </Container.Section>

      {/* CTA Section */}
      <Container.Section padding="content" background="primary">
        <Container size="narrow">
          <FadeIn delay={0.2} direction="up" fullWidth>
            <div className="text-center">
              <Typography.H2 color="white" className="mb-4">
                Ready to Start Your BIM Career?
              </Typography.H2>
              <Typography.Lead color="white" className="mb-6 opacity-90">
                Join thousands of students who have transformed their careers with BIM Bytes Academy
              </Typography.Lead>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/courses" className="btn-white px-8 py-4 text-lg font-bold inline-flex items-center justify-center">
                  Explore Courses
                </a>
                <a href="/contact" className="px-8 py-4 rounded-xl border-2 border-white text-white font-bold text-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center">
                  Contact Us
                </a>
              </div>
            </div>
          </FadeIn>
        </Container>
      </Container.Section>

      <Footer />
    </>
  );
};

export default WhyChooseUs;